import { useMemo, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import { Search } from "lucide-react"
import { Button } from "../../../ui"
import { formatVND } from "../../../constants"
import GrStatusBadge from "./GrStatusBadge"
import { sameSupplier } from "./paymentRequestModel"

const supplierOf = (po) => po.vendorName || po.supplierName || "—"
const totalOf = (po) => po.total ?? po.totalAmount

// Multi-select picker for payable POs. Already-added rows stay checked and
// disabled. Once one supplier is in the request, other suppliers are blocked
// — a payment request pays one supplier.
const PoSelectionModal = ({ open, purchases = [], loading, selectedIds = [], anchor, onClose, onAdd }) => {
  const [q, setQ] = useState("")
  const [picked, setPicked] = useState([])

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase()
    return (purchases || [])
      .filter((p) => p.remainingToPay == null || Number(p.remainingToPay) > 0)
      .filter((p) => {
        if (!query) return true
        return `${p.poCode || ""} ${supplierOf(p)}`.toLowerCase().includes(query)
      })
  }, [purchases, q])

  const toggle = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const close = () => {
    setPicked([])
    setQ("")
    onClose()
  }

  const confirm = () => {
    const chosen = (purchases || []).filter((p) => picked.includes(String(p.id)))
    onAdd(chosen)
    setPicked([])
    setQ("")
  }

  return (
    // The dialog portals outside the page's `.pc-root`, where the design tokens
    // live — without it the primary button renders white-on-white.
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: "pc-root", style: { minHeight: "auto", background: "var(--pc-surface)" } }}
    >
      <DialogTitle>Add purchase orders</DialogTitle>
      <DialogContent>
        <p className="prc-help" style={{ marginTop: 0 }}>
          Select one or more purchase orders from the same supplier. Goods receipt status is loaded from each PO.
        </p>
        <div className="prc-search" style={{ maxWidth: "none", margin: "10px 0" }}>
          <Search aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search PO number or supplier"
            aria-label="Search purchase orders"
          />
        </div>
        <div className="prc-table-wrap" style={{ border: "1px solid var(--pc-border)", borderRadius: 10 }}>
          <table className="prc-table">
            <thead>
              <tr>
                <th className="prc-check" />
                <th>PO Number</th>
                <th>Supplier</th>
                <th className="prc-num">PO Total</th>
                <th className="prc-num">Remaining</th>
                <th>Goods Receipt Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="prc-empty">
                    Loading purchase orders…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="prc-empty">
                    No payable purchase orders match this search.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((p) => {
                  const id = String(p.id)
                  const already = selectedIds.includes(id)
                  const blocked = !already && anchor && !sameSupplier(anchor, {
                    supplierId: p.vendorId ?? p.supplierId,
                    supplierName: supplierOf(p),
                  })
                  const checked = already || picked.includes(id)
                  return (
                    <tr key={id} style={{ opacity: blocked ? 0.45 : 1 }}>
                      <td className="prc-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={already || blocked}
                          onChange={() => toggle(id)}
                          aria-label={`Select ${p.poCode || id}`}
                        />
                      </td>
                      <td className="prc-po">{p.poCode || `PO #${id}`}</td>
                      <td>
                        {supplierOf(p)}
                        {blocked && <span className="prc-sub">Different supplier</span>}
                      </td>
                      <td className="prc-num">{formatVND(totalOf(p))}</td>
                      <td className="prc-num">{formatVND(p.remainingToPay)}</td>
                      <td>
                        <GrStatusBadge purchaseId={p.id} />
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" onClick={confirm} disabled={picked.length === 0}>
          Add {picked.length ? `${picked.length} ` : ""}PO{picked.length === 1 ? "" : "s"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PoSelectionModal
